import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { MONTHLY_PRICE } from '../constants/pricing';

const CACHE_KEY    = 'sanoire_admin_stats_v5';
const CACHE_TTL    = 15 * 60 * 1000;
const PASSES_KEY   = 'sanoire_admin_passes_v3';
const PASSES_TTL   = 3 * 60 * 1000;
const SAVED_KEY    = 'sanoire_admin_saved_v3';
const SAVED_TTL    = 5 * 60 * 1000;
const SANA_KEY     = 'sanoire_admin_sana_v3';
const SANA_TTL     = 5 * 60 * 1000;
const COMM_KEY     = 'sanoire_admin_community_v3';
const COMM_TTL     = 3 * 60 * 1000;
const PROFILES_KEY = 'sanoire_admin_profiles_v2';
const PROFILES_TTL = 10 * 60 * 1000;

function getCached(key, ttl) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > ttl) return null;
    return data;
  } catch (_e) { return null; }
}

function setCache(data, key) {
  try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch (_e) {}
}

export function bustAllCaches() {
  [CACHE_KEY, PASSES_KEY, SAVED_KEY, SANA_KEY, COMM_KEY, PROFILES_KEY].forEach(k => {
    try { localStorage.removeItem(k); } catch (_e) {}
  });
}

async function fetchFreshnessCounts() {
  try {
    const { data, error } = await supabase.rpc('get_admin_stats');
    if (!error && data && data.total) {
      return {
        total: data.total, fresh: data.fresh, stale: data.stale, never: data.never,
        mid: Math.max(0, data.total - data.fresh - data.stale - data.never),
        countryCount: data.countryCount || null,
        topCountries: data.byCountry || null,
        topTypes: data.byType || null,
        fromRpc: true,
      };
    }
  } catch (_e) {}
  try {
    const { count } = await supabase.from('Locations').select('*', { count: 'exact', head: true });
    return { total: count||0, fresh:null, stale:null, never:null, mid:null, countryCount:null, topCountries:null, topTypes:null, fromRpc:false };
  } catch (_e) { return null; }
}

export function useLocationsStats() {
  const [data, setData]       = useState(() => getCached(CACHE_KEY, CACHE_TTL));
  const [loading, setLoading] = useState(() => !getCached(CACHE_KEY, CACHE_TTL));

  const refresh = useCallback(async () => {
    const next = await fetchFreshnessCounts();
    if (next) { setData(next); setCache(next, CACHE_KEY); }
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const delay = getCached(CACHE_KEY, CACHE_TTL) ? 1000 : 0;
    const timer = setTimeout(() => { if (!cancelled) refresh(); }, delay);
    function onVisible() {
      if (document.visibilityState === 'visible') { localStorage.removeItem(CACHE_KEY); if (!cancelled) refresh(); }
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; clearTimeout(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [refresh]);

  return { data, loading, refresh };
}

export function usePassesStats() {
  const [data, setData]       = useState(() => getCached(PASSES_KEY, PASSES_TTL));
  const [loading, setLoading] = useState(() => !getCached(PASSES_KEY, PASSES_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: res, error } = await supabase.rpc('admin_pass_stats');
        if (!cancelled && !error && res) {
          const next = {
            total: res.total || 0, active: res.active || 0, thisWeek: res.thisWeek || 0,
            expiring30: res.expiring30 || 0, expiring48h: 0, expiring48hRows: [],
            byType: res.byType || [], uniqueUsers: res.uniqueUsers || 0,
          };
          setData(next); setCache(next, PASSES_KEY);
        }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(PASSES_KEY, PASSES_TTL) ? 500 : 0;
    const t = setTimeout(load, delay);
    const ch = supabase.channel('passes-rt')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'user_passes' }, () => { localStorage.removeItem(PASSES_KEY); load(); })
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'user_passes' }, () => { localStorage.removeItem(PASSES_KEY); load(); })
      .subscribe();
    function onVisible(){ if(document.visibilityState==='visible'){ localStorage.removeItem(PASSES_KEY); if(!cancelled) load(); } }
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled=true; clearTimeout(t); supabase.removeChannel(ch); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  return { data: data || { total:0, active:0, thisWeek:0, expiring30:0, byType:[], uniqueUsers:0 }, loading };
}

export function useSavedStats() {
  const [data, setData]       = useState(() => getCached(SAVED_KEY, SAVED_TTL));
  const [loading, setLoading] = useState(() => !getCached(SAVED_KEY, SAVED_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: res, error } = await supabase.rpc('admin_saved_stats');
        if (!cancelled && !error && res) {
          const next = { total: res.total || 0, uniqueLocations: res.uniqueLocations || 0, uniqueUsers: res.uniqueUsers || 0, topLocations: res.topLocations || [] };
          setData(next); setCache(next, SAVED_KEY);
        }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(SAVED_KEY, SAVED_TTL) ? 500 : 0;
    const t = setTimeout(load, delay);
    const ch = supabase.channel('saves-rt')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'saved_locations' }, () => { localStorage.removeItem(SAVED_KEY); load(); })
      .on('postgres_changes', { event:'DELETE', schema:'public', table:'saved_locations' }, () => { localStorage.removeItem(SAVED_KEY); load(); })
      .subscribe();
    return () => { cancelled=true; clearTimeout(t); supabase.removeChannel(ch); };
  }, []);

  return { data: data || { total:0, uniqueLocations:0, uniqueUsers:0, topLocations:[] }, loading };
}

export function useSanaStats() {
  const [data, setData]       = useState(() => getCached(SANA_KEY, SANA_TTL));
  const [loading, setLoading] = useState(() => !getCached(SANA_KEY, SANA_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const {count:total}=await supabase.from('sana_invocations').select('*',{count:'exact',head:true});
        const today=new Date();today.setHours(0,0,0,0);
        const {count:todayCount}=await supabase.from('sana_invocations').select('*',{count:'exact',head:true}).gte('created_at',today.toISOString());
        const {data:rows}=await supabase.from('sana_invocations').select('query,result_count').order('created_at',{ascending:false}).limit(200);
        const qMap={}; let withResults=0;
        (rows||[]).forEach(q=>{if(q.query)qMap[q.query]=(qMap[q.query]||0)+1;if((q.result_count||0)>0)withResults++;});
        const topQueries=Object.entries(qMap).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([query,count])=>({query,count}));
        const resultRate=rows?.length?Math.round((withResults/rows.length)*100):0;
        const next={total:total||0,todayCount:todayCount||0,topQueries,resultRate};
        if(!cancelled){setData(next);setCache(next,SANA_KEY);}
      } catch(_e){}
      finally{if(!cancelled)setLoading(false);}
    }
    const delay=getCached(SANA_KEY,SANA_TTL)?500:0;
    const t=setTimeout(load,delay);
    const ch=supabase.channel('sana-rt')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'sana_invocations'},()=>{localStorage.removeItem(SANA_KEY);load();})
      .subscribe();
    return ()=>{cancelled=true;clearTimeout(t);supabase.removeChannel(ch);};
  },[]);

  return { data: data||{total:0,todayCount:0,topQueries:[],resultRate:0}, loading };
}

export function useCommunityStats() {
  const [data, setData]       = useState(() => getCached(COMM_KEY, COMM_TTL));
  const [loading, setLoading] = useState(() => !getCached(COMM_KEY, COMM_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Drift family (the tables the native app actually writes) via the
        // admin_drift_overview RPC — the legacy community_* tables are dead.
        const { data: ov } = await supabase.rpc('admin_drift_overview');
        const next={totalProfiles:(ov&&ov.members_total)||0,totalMessages:(ov&&ov.messages_total)||0,totalChannels:(ov&&ov.channels)||0,todayMessages:(ov&&ov.messages_24h)||0};
        if(!cancelled){setData(next);setCache(next,COMM_KEY);}
      } catch(_e){}
      finally{if(!cancelled)setLoading(false);}
    }
    const delay=getCached(COMM_KEY,COMM_TTL)?500:0;
    const t=setTimeout(load,delay);
    const ch=supabase.channel('community-rt')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'drift_channel_messages'},()=>{localStorage.removeItem(COMM_KEY);load();})
      .subscribe();
    return ()=>{cancelled=true;clearTimeout(t);supabase.removeChannel(ch);};
  },[]);

  return { data: data||{totalProfiles:0,totalMessages:0,totalChannels:0,todayMessages:0}, loading };
}

export function useSupportStats() {
  const [data, setData]       = useState({open:0,inProgress:0,resolved:0,total:0,unassigned:0});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const {data:tickets}=await supabase.rpc('admin_get_tickets');
        const counts={open:0,in_progress:0,resolved:0}; let unassigned=0;
        (tickets||[]).forEach(t=>{counts[t.status]=(counts[t.status]||0)+1;if(!t.assigned_to&&t.status!=='resolved')unassigned++;});
        const next={open:counts.open,inProgress:counts.in_progress,resolved:counts.resolved,total:(tickets||[]).length,unassigned};
        if(!cancelled)setData(next);
      } catch(_e){}
      finally{if(!cancelled)setLoading(false);}
    }
    load();
    const ch=supabase.channel('tickets-rt')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'support_tickets'},load)
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'support_tickets'},load)
      .subscribe();
    const interval=setInterval(load,60000);
    return ()=>{cancelled=true;clearInterval(interval);supabase.removeChannel(ch);};
  },[]);

  return { data, loading };
}

export function useWaitlistStats() {
  const [count, setCount]     = useState(null);
  const [waiting, setWaiting] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const {count:total}=await supabase.from('waitlist').select('*',{count:'exact',head:true});
        const {count:w}=await supabase.from('waitlist').select('*',{count:'exact',head:true}).eq('status','waiting');
        if(!cancelled){setCount(total||0);setWaiting(w||0);}
      } catch(_e){}
    }
    load();
    const ch=supabase.channel('waitlist-rt')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'waitlist'},load)
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'waitlist'},load)
      .subscribe();
    return ()=>{cancelled=true;supabase.removeChannel(ch);};
  },[]);

  return { count, waiting };
}

export function useAlertCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try { const {data}=await supabase.rpc('get_alert_count'); if(!cancelled)setCount(typeof data==='number'?data:0); } catch(_e){}
    }
    load();
    const interval=setInterval(load,5*60*1000);
    return ()=>{cancelled=true;clearInterval(interval);};
  },[]);
  return count;
}

export function useHealthProfileStats() {
  const [data, setData]       = useState(() => getCached(PROFILES_KEY, PROFILES_TTL));
  const [loading, setLoading] = useState(() => !getCached(PROFILES_KEY, PROFILES_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const {count:total}=await supabase.from('health_profiles').select('*',{count:'exact',head:true});
        const {data:rows}=await supabase.from('health_profiles')
          .select('conditions,medications_allergies,blood_type,insurance_type,preferred_language,wellness_interests,emergency_contact,nationality,dietary_restrictions,medication_storage,mobility_needs')
          .limit(500);
        const FIELDS=['conditions','medications_allergies','blood_type','insurance_type','preferred_language','wellness_interests','emergency_contact','nationality','dietary_restrictions','medication_storage','mobility_needs'];
        let totalFields=0;
        (rows||[]).forEach(r=>{FIELDS.forEach(f=>{if(r[f])totalFields++;});});
        const completionRate=rows?.length?Math.round((totalFields/(rows.length*FIELDS.length))*100):0;
        const condMap={};
        (rows||[]).forEach(r=>{if(!r.conditions)return;r.conditions.split(',').forEach(c=>{const k=c.trim();if(k&&k!=='None')condMap[k]=(condMap[k]||0)+1;});});
        const topConditions=Object.entries(condMap).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([condition,count])=>({condition,count}));
        const next={total:total||0,completionRate,topConditions};
        if(!cancelled){setData(next);setCache(next,PROFILES_KEY);}
      } catch(_e){}
      finally{if(!cancelled)setLoading(false);}
    }
    const delay=getCached(PROFILES_KEY,PROFILES_TTL)?500:0;
    const t=setTimeout(load,delay);
    return ()=>{cancelled=true;clearTimeout(t);};
  },[]);

  return { data: data||{total:0,completionRate:0,topConditions:[]}, loading };
}

export function useActivityFeed(limit) {
  const feedLimit = limit || 50;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const {data}=await supabase.rpc('admin_get_activity',{p_limit:feedLimit});
      setEvents(data||[]);
    } catch(_e){}
    setLoading(false);
  },[feedLimit]);

  useEffect(() => {
    load();
    const ch=supabase.channel('activity-feed-rt')
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'admin_activity_log'},()=>load())
      .subscribe();
    return ()=>supabase.removeChannel(ch);
  },[load]);

  return { events, loading, refresh: load };
}

// ── Live counts via Supabase Realtime ────────────────────────────────────────
// Fires a callback whenever a new row is inserted into a table
export function useRealtimeCount(table, initialCount) {
  const [count, setCount] = useState(initialCount || 0);

  useEffect(() => {
    // Get initial count
    let cancelled = false;
    supabase.from(table).select('*', { count: 'exact', head: true })
      .then(({ count: c }) => { if (!cancelled && c !== null) setCount(c); });

    // Subscribe to inserts
    const channel = supabase.channel('rt_' + table)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table }, () => {
        supabase.from(table).select('*', { count: 'exact', head: true })
          .then(({ count: c }) => { if (!cancelled && c !== null) setCount(c); });
      })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [table]);

  return count;
}

// ── Live activity feed ────────────────────────────────────────────────────────

// ── Role hook ─────────────────────────────────────────────────────────────────
// Returns the current admin user's role — resolves on first render
export function useMyRole() {
  const [role, setRole] = useState('support');
  const [email, setEmail] = useState('');

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled && data?.user) {
        const em = data.user.email || '';
        setEmail(em);
        // Inline role resolution (mirrors roles.js logic without importing it)
        try {
          const map = JSON.parse(localStorage.getItem('sanoire_team_roles') || '{}');
          if (map[em]) { setRole(map[em]); return; }
        } catch (_e) {}
        if (em.endsWith('@sanoire.com') || em.endsWith('@blynkstudio.com')) {
          setRole('admin');
        } else {
          setRole('support');
        }
      }
    });
    return () => { cancelled = true; };
  }, []);

  const canFinancial  = role === 'c_suite' || role === 'admin';
  const canData       = role === 'c_suite' || role === 'admin' || role === 'data';
  const canMarketing  = role === 'c_suite' || role === 'admin' || role === 'marketing';
  const canSupport    = true; // everyone

  return { role, email, canFinancial, canData, canMarketing, canSupport };
}


// ── Revenue from access_events (real purchase prices) ─────────────────────────
const REV_KEY = 'sanoire_admin_revenue_v1';
const REV_TTL = 5 * 60 * 1000;

// Two plans, both Google Play subscriptions. The breakdown keeps ANNUAL in the
// "one-time" bucket (paid once a year) and MONTHLY in "recurring".
const ONE_TIME_SKUS  = ['ANNUAL'];
const RECURRING_SKUS = ['MONTHLY'];

export function useRevenueStats() {
  const [data, setData]       = useState(() => getCached(REV_KEY, REV_TTL));
  const [loading, setLoading] = useState(() => !getCached(REV_KEY, REV_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: rows } = await supabase
          .from('access_events')
          .select('pass_type,pass_label,price_usd,days_granted,created_at,email')
          .order('created_at', { ascending: false })
          .limit(2000);
        const { count: refunds } = await supabase
          .from('refund_requests').select('*', { count: 'exact', head: true });
        // user_passes drives LTV / churn / trial conversion — access_events alone
        // can't tell us which MONTHLY users are still active vs cancelled.
        const { data: passRows } = await supabase
          .from('user_passes')
          .select('user_id,pass_type,created_at,expires_at,status')
          .in('pass_type', ['MONTHLY', 'monthly']);
        const now = new Date();
        const d30 = new Date(now - 30 * 86400000).toISOString();
        const d7  = new Date(now - 7  * 86400000).toISOString();
        const typeMap = {}, emails = new Set();
        let totalRev = 0, rev30 = 0, rev7 = 0;
        let oneTimeRev30 = 0, recurringRev30 = 0;
        (rows || []).forEach(r => {
          const p = parseFloat(r.price_usd) || 0;
          totalRev += p;
          if (r.created_at > d30) {
            rev30 += p;
            if (ONE_TIME_SKUS.includes(String(r.pass_type || '').toUpperCase()))  oneTimeRev30  += p;
            if (RECURRING_SKUS.includes(String(r.pass_type || '').toUpperCase())) recurringRev30 += p;
          }
          if (r.created_at > d7)  rev7  += p;
          const k = r.pass_type || 'unknown';
          if (!typeMap[k]) typeMap[k] = { type: k, label: r.pass_label || k, count: 0, revenue: 0 };
          typeMap[k].count++;
          typeMap[k].revenue += p;
          if (r.email) emails.add(r.email);
        });
        const byType = Object.values(typeMap).sort((a, b) => b.revenue - a.revenue);
        const uniqueUsers = emails.size;
        const arpu = uniqueUsers ? (totalRev / uniqueUsers).toFixed(2) : '0.00';

        // ── MONTHLY LTV / churn / trial conversion ────────────────────────
        // LTV proxy: months_active * MONTHLY_PRICE (list), created_at -> min(expires_at, now)
        // for each MONTHLY pass row. Aggregated per user_id, then averaged.
        const monthlyByUser = {}; // user_id -> { firstCreated, lastExpires, status }
        (passRows || []).filter(r => String(r.pass_type || '').toUpperCase() === 'MONTHLY').forEach(r => {
          const uid = r.user_id || 'unknown';
          const created = r.created_at ? new Date(r.created_at) : null;
          const expires = r.expires_at ? new Date(r.expires_at) : null;
          if (!monthlyByUser[uid]) monthlyByUser[uid] = { firstCreated: created, lastExpires: expires, status: r.status };
          else {
            if (created && (!monthlyByUser[uid].firstCreated || created < monthlyByUser[uid].firstCreated))
              monthlyByUser[uid].firstCreated = created;
            if (expires && (!monthlyByUser[uid].lastExpires || expires > monthlyByUser[uid].lastExpires))
              monthlyByUser[uid].lastExpires = expires;
          }
        });
        let monthlyUserCount = 0, monthlyLtvSum = 0;
        let activeMonthlyNow = 0;
        const churnWindowStart = new Date(now - 30 * 86400000);
        let churnedLast30 = 0, activeAtWindowStart = 0;
        Object.values(monthlyByUser).forEach(u => {
          if (!u.firstCreated) return;
          monthlyUserCount++;
          const endDate = (u.lastExpires && u.lastExpires < now) ? u.lastExpires : now;
          const monthsActive = Math.max(0, (endDate - u.firstCreated) / (30 * 86400000));
          monthlyLtvSum += monthsActive * MONTHLY_PRICE;
          if (u.lastExpires && u.lastExpires > now) activeMonthlyNow++;
          // Churn: had an active sub at start of window, expired during window
          if (u.firstCreated < churnWindowStart) {
            activeAtWindowStart++;
            if (u.lastExpires && u.lastExpires > churnWindowStart && u.lastExpires < now) churnedLast30++;
          }
        });
        const monthlyLtvAvg = monthlyUserCount ? (monthlyLtvSum / monthlyUserCount).toFixed(2) : '0.00';
        const monthlyChurnPct = activeAtWindowStart
          ? +((churnedLast30 / activeAtWindowStart) * 100).toFixed(1)
          : 0;
        // Recurring revenue (MRR proxy): active MONTHLY users * MONTHLY_PRICE (list)
        const recurringMrr = activeMonthlyNow * MONTHLY_PRICE;

        // Trial conversion: the only trial is the invite offer on MONTHLY. Invite
        // claims that started a trial in the last 60 days, and how many are paying.
        let trialCount = 0, trialConverted = 0;
        try {
          const { data: ts } = await supabase.rpc('admin_invite_trial_stats', { p_days: 60 });
          trialCount = (ts && ts.trials) || 0;
          trialConverted = (ts && ts.converted) || 0;
        } catch (_e) { /* leave at 0 */ }
        const trialConversionPct = trialCount
          ? +((trialConverted / trialCount) * 100).toFixed(1)
          : 0;

        const next = {
          total: rows?.length || 0, totalRev, rev30, rev7,
          byType, uniqueUsers, arpu,
          refunds: refunds || 0,
          recent: (rows || []).slice(0, 10),
          // ── New LTV / recurring fields (May 2026) ──
          oneTimeRev30,
          recurringRev30,
          recurringMrr,                    // MRR proxy: active MONTHLY * MONTHLY_PRICE
          monthlyActiveUsers: activeMonthlyNow,
          monthlyLtvAvg,                   // $ avg over all MONTHLY users (lifetime)
          monthlyChurnPct,                 // % of MONTHLY subs that ended in last 30d
          trialConversionPct,              // % of trials in last 60d that became paid MONTHLY
          trialCount,
          trialConverted,
        };
        if (!cancelled) { setData(next); setCache(next, REV_KEY); }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(REV_KEY, REV_TTL) ? 500 : 0;
    const t = setTimeout(load, delay);
    const ch = supabase.channel('access-events-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'access_events' }, () => {
        localStorage.removeItem(REV_KEY); load();
      }).subscribe();
    return () => { cancelled = true; clearTimeout(t); supabase.removeChannel(ch); };
  }, []);

  return { data: data || { total: 0, totalRev: 0, rev30: 0, rev7: 0, byType: [], uniqueUsers: 0, arpu: '0.00', refunds: 0, recent: [], oneTimeRev30: 0, recurringRev30: 0, recurringMrr: 0, monthlyActiveUsers: 0, monthlyLtvAvg: '0.00', monthlyChurnPct: 0, trialConversionPct: 0, trialCount: 0, trialConverted: 0 }, loading };
}


// ── Search invocations analytics ──────────────────────────────────────────────
const SEARCH_KEY = 'sanoire_admin_search_v1';
const SEARCH_TTL = 5 * 60 * 1000;

export function useSearchStats() {
  const [data, setData]       = useState(() => getCached(SEARCH_KEY, SEARCH_TTL));
  const [loading, setLoading] = useState(() => !getCached(SEARCH_KEY, SEARCH_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { count: total } = await supabase
          .from('search_invocations').select('*', { count: 'exact', head: true });
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const { count: todayCount } = await supabase
          .from('search_invocations').select('*', { count: 'exact', head: true })
          .gte('created_at', today.toISOString());
        const { data: rows } = await supabase
          .from('search_invocations')
          .select('query,country,result_count,wellness_mode,telehealth_only,emergency_only,is_near_me,created_at')
          .order('created_at', { ascending: false })
          .limit(500);
        const qMap = {}, countryMap = {};
        let zeroResults = 0, nearMe = 0, wellness = 0, telehealth = 0, emergency = 0;
        (rows || []).forEach(r => {
          if (r.query) qMap[r.query] = (qMap[r.query] || 0) + 1;
          if (r.country) countryMap[r.country] = (countryMap[r.country] || 0) + 1;
          if ((r.result_count || 0) === 0) zeroResults++;
          if (r.is_near_me) nearMe++;
          if (r.wellness_mode) wellness++;
          if (r.telehealth_only) telehealth++;
          if (r.emergency_only) emergency++;
        });
        const n = rows?.length || 1;
        const topQueries = Object.entries(qMap).sort((a, b) => b[1] - a[1]).slice(0, 8)
          .map(([query, count]) => ({ query, count }));
        const topCountries = Object.entries(countryMap).sort((a, b) => b[1] - a[1]).slice(0, 5)
          .map(([country, count]) => ({ country, count }));
        const next = {
          total: total || 0, todayCount: todayCount || 0, topQueries, topCountries,
          zeroResultRate: Math.round((zeroResults / n) * 100),
          nearMeRate: Math.round((nearMe / n) * 100),
          wellnessRate: Math.round((wellness / n) * 100),
          telehealthRate: Math.round((telehealth / n) * 100),
          emergencyRate: Math.round((emergency / n) * 100),
        };
        if (!cancelled) { setData(next); setCache(next, SEARCH_KEY); }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(SEARCH_KEY, SEARCH_TTL) ? 500 : 0;
    const t = setTimeout(load, delay);
    const ch = supabase.channel('search-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'search_invocations' },
        () => { localStorage.removeItem(SEARCH_KEY); load(); })
      .subscribe();
    return () => { cancelled = true; clearTimeout(t); supabase.removeChannel(ch); };
  }, []);

  return { data: data || { total: 0, todayCount: 0, topQueries: [], topCountries: [], zeroResultRate: 0 }, loading };
}

// ── Sana invocations deep analytics ──────────────────────────────────────────
const SANA_DEEP_KEY = 'sanoire_admin_sana_deep_v1';
const SANA_DEEP_TTL = 10 * 60 * 1000;

export function useSanaDeepStats() {
  const [data, setData]       = useState(() => getCached(SANA_DEEP_KEY, SANA_DEEP_TTL));
  const [loading, setLoading] = useState(() => !getCached(SANA_DEEP_KEY, SANA_DEEP_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: rows } = await supabase
          .from('sana_invocations')
          .select('intent_type,country,result_count,latency_ms,is_near_me,profile_used,intent_emergency,fallback_level,created_at,tokens_used')
          .order('created_at', { ascending: false })
          .limit(1000);
        const intentMap = {}, countryMap = {};
        let totalLatency = 0, latencyCount = 0, totalTokens = 0;
        let profileUsed = 0, nearMe = 0, emergency = 0, withResults = 0;
        (rows || []).forEach(r => {
          if (r.intent_type) intentMap[r.intent_type] = (intentMap[r.intent_type] || 0) + 1;
          if (r.country) countryMap[r.country] = (countryMap[r.country] || 0) + 1;
          if (r.latency_ms) { totalLatency += r.latency_ms; latencyCount++; }
          if (r.tokens_used) totalTokens += r.tokens_used;
          if (r.profile_used) profileUsed++;
          if (r.is_near_me) nearMe++;
          if (r.intent_emergency) emergency++;
          if ((r.result_count || 0) > 0) withResults++;
        });
        const n = rows?.length || 1;
        const topIntents = Object.entries(intentMap).sort((a, b) => b[1] - a[1]).slice(0, 6)
          .map(([intent, count]) => ({ intent, count }));
        const topCountries = Object.entries(countryMap).sort((a, b) => b[1] - a[1]).slice(0, 5)
          .map(([country, count]) => ({ country, count }));
        const next = {
          total: rows?.length || 0,
          avgLatencyMs: latencyCount ? Math.round(totalLatency / latencyCount) : null,
          totalTokens,
          profileRate: Math.round((profileUsed / n) * 100),
          nearMeRate: Math.round((nearMe / n) * 100),
          emergencyRate: Math.round((emergency / n) * 100),
          resultRate: Math.round((withResults / n) * 100),
          topIntents, topCountries,
        };
        if (!cancelled) { setData(next); setCache(next, SANA_DEEP_KEY); }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(SANA_DEEP_KEY, SANA_DEEP_TTL) ? 500 : 0;
    const t = setTimeout(load, delay);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  return { data: data || { total: 0, avgLatencyMs: null, totalTokens: 0, profileRate: 0, topIntents: [], topCountries: [] }, loading };
}

// ── Pipeline runs analytics ───────────────────────────────────────────────────
const PIPELINE_KEY = 'sanoire_admin_pipeline_v1';
const PIPELINE_TTL = 2 * 60 * 1000;

export function usePipelineStats() {
  const [data, setData]       = useState(() => getCached(PIPELINE_KEY, PIPELINE_TTL));
  const [loading, setLoading] = useState(() => !getCached(PIPELINE_KEY, PIPELINE_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: runs } = await supabase
          .from('pipeline_runs')
          .select('pipeline,country,status,started_at,finished_at,total,processed,succeeded,failed')
          .order('started_at', { ascending: false })
          .limit(50);
        const recent = (runs || []).slice(0, 10);
        const running = (runs || []).filter(r => r.status === 'running').length;
        const failed  = (runs || []).filter(r => r.status === 'failed').length;
        const { count: pendingJobs } = await supabase
          .from('pipeline_jobs').select('*', { count: 'exact', head: true }).eq('status', 'queued');
        const next = { recent, running, failed, pendingJobs: pendingJobs || 0 };
        if (!cancelled) { setData(next); setCache(next, PIPELINE_KEY); }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(PIPELINE_KEY, PIPELINE_TTL) ? 500 : 0;
    const t = setTimeout(load, delay);
    const ch = supabase.channel('pipeline-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'pipeline_runs' },
        () => { localStorage.removeItem(PIPELINE_KEY); load(); })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'pipeline_runs' },
        () => { localStorage.removeItem(PIPELINE_KEY); load(); })
      .subscribe();
    return () => { cancelled = true; clearTimeout(t); supabase.removeChannel(ch); };
  }, []);

  return { data: data || { recent: [], running: 0, failed: 0, pendingJobs: 0 }, loading };
}


// -- Store billing (RevenueCat + native validators) -- added Jun 2026 --------
const STORE_BILLING_KEY = 'sanoire_admin_storebilling_v1';
const STORE_BILLING_TTL = 5 * 60 * 1000;

export function useStoreBillingStats() {
  const [data, setData] = useState(() => getCached(STORE_BILLING_KEY, STORE_BILLING_TTL));
  const [loading, setLoading] = useState(() => !getCached(STORE_BILLING_KEY, STORE_BILLING_TTL));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res, error } = await supabase.rpc('admin_store_billing_stats', { p_period: '30d' });
        if (!cancelled && !error && res && !res.error) {
          setData(res);
          setCache(res, STORE_BILLING_KEY);
        }
      } catch (_e) {
        /* keep cached */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { data, loading };
}


// ── Command Center: geographic + directory quality (single RPC) ───────────────
const CMDGEO_KEY = 'sanoire_admin_cmdgeo_v1';
const CMDGEO_TTL = 15 * 60 * 1000;

export function useCommandGeo() {
  const [data, setData]       = useState(() => getCached(CMDGEO_KEY, CMDGEO_TTL));
  const [loading, setLoading] = useState(() => !getCached(CMDGEO_KEY, CMDGEO_TTL));

  const refresh = useCallback(async () => {
    try {
      const { data: res, error } = await supabase.rpc('admin_command_geo');
      if (!error && res) { setData(res); setCache(res, CMDGEO_KEY); }
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const delay = getCached(CMDGEO_KEY, CMDGEO_TTL) ? 800 : 0;
    const t = setTimeout(() => { if (!cancelled) refresh(); }, delay);
    return () => { cancelled = true; clearTimeout(t); };
  }, [refresh]);

  return { data, loading, refresh };
}

// ── Command Center: 14-day engagement series (Sana + Search) ──────────────────
const CMDENG_KEY = 'sanoire_admin_cmdeng_v1';
const CMDENG_TTL = 10 * 60 * 1000;

export function useEngagementSeries() {
  const [data, setData]       = useState(() => getCached(CMDENG_KEY, CMDENG_TTL));
  const [loading, setLoading] = useState(() => !getCached(CMDENG_KEY, CMDENG_TTL));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const since = new Date(Date.now() - 14 * 86400000); since.setHours(0,0,0,0);
        const sinceISO = since.toISOString();
        const [{ data: sana }, { data: search }] = await Promise.all([
          supabase.from('sana_invocations').select('created_at').gte('created_at', sinceISO).limit(5000),
          supabase.from('search_invocations').select('created_at').gte('created_at', sinceISO).limit(5000),
        ]);
        const days = [];
        for (let i = 13; i >= 0; i--) {
          const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - i);
          days.push({ key: d.toISOString().slice(0,10), label: d.toLocaleDateString('en-US',{ day:'numeric' }), sana: 0, search: 0 });
        }
        const idx = {}; days.forEach((d,i)=>{ idx[d.key]=i; });
        (sana||[]).forEach(r=>{ const k=(r.created_at||'').slice(0,10); if(idx[k]!=null) days[idx[k]].sana++; });
        (search||[]).forEach(r=>{ const k=(r.created_at||'').slice(0,10); if(idx[k]!=null) days[idx[k]].search++; });
        const next = { days, totalSana: (sana||[]).length, totalSearch: (search||[]).length };
        if (!cancelled) { setData(next); setCache(next, CMDENG_KEY); }
      } catch (_e) {}
      finally { if (!cancelled) setLoading(false); }
    }
    const delay = getCached(CMDENG_KEY, CMDENG_TTL) ? 800 : 0;
    const t = setTimeout(load, delay);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  return { data: data || { days: [], totalSana: 0, totalSearch: 0 }, loading };
}
